import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import * as SimpleMDE from 'simplemde';

/**
 * Markdown text editor. Usage:
 *
 * <app-markdown-editor [id]="'mytextfieldeditor'" [(content)]="myobj.mytextfield"></app-markdown-editor>
 */
@Component({
  selector: 'app-markdown-editor',
  templateUrl: './markdown-editor.component.html',
  styleUrls: ['./markdown-editor.component.css']
})
export class MarkdownEditorComponent implements AfterViewInit, OnChanges {

  @Input()
  id: string;

  @Input()
  content: string;

  @Input()
  shortEditor?: boolean;

  @Output()
  contentChange = new EventEmitter<string>();

  private markdownEditor: any = null;

  constructor() {}

  ngAfterViewInit() {
    this.markdownEditor = new SimpleMDE({
      element: document.getElementById(this.id),
      status: false,
      spellChecker: true,
      toolbar: [
        {
          name: 'bold',
          action: SimpleMDE.toggleBold,
          className: 'fa fa-bold',
          title: 'Bold',
          default: true
        },
        {
          name: 'italic',
          action: SimpleMDE.toggleItalic,
          className: 'fa fa-italic',
          title: 'Italic',
          default: true
        },
        {
          name: 'heading',
          action: SimpleMDE.toggleHeadingSmaller,
          className: 'fa fa-header',
          title: 'Heading',
          default: true
        },
        '|',
        {
          name: 'quote',
          action: SimpleMDE.toggleBlockquote,
          className: 'fa fa-quote-left',
          title: 'Quote',
          default: true
        },
        {
          name: 'unordered-list',
          action: SimpleMDE.toggleUnorderedList,
          className: 'fa fa-list-ul',
          title: 'Generic List',
          default: true
        },
        {
          name: 'ordered-list',
          action: SimpleMDE.toggleOrderedList,
          className: 'fa fa-list-ol',
          title: 'Numbered List',
          default: true
        },
        '|',
        {
          name: 'link',
          action: SimpleMDE.drawLink,
          className: 'fa fa-link',
          title: 'Create Link',
          default: true
        },
        {
          name: 'image',
          action: SimpleMDE.drawImage,
          className: 'fa fa-picture-o',
          title: 'Insert Image',
          default: true
        },
        {
          name: 'table',
          action: SimpleMDE.drawTable,
          className: 'fa fa-table',
          title: 'Insert Table'
        },
        '|',
        {
          name: 'preview',
          action: SimpleMDE.togglePreview,
          className: 'fa fa-eye no-disable',
          title: 'Toggle Preview',
          default: true
        },
        '|',
        {
          name: 'guide',
          action: 'https://simplemde.com/markdown-guide',
          className: 'fa fa-question-circle',
          title: 'Markdown Guide',
          default: true
        }
      ]
    });

    this.markdownEditor.value(this.content);

    this.markdownEditor.codemirror.on('change', (_codemirror: any, change: { origin?: string }) => {
      this.content = this.markdownEditor.value();

      // CodeMirror also fires 'change' when WE set the value (from
      // ngOnChanges, or the initial value above); those carry the 'setValue'
      // origin. Only user edits (typing, paste, toolbar actions) are reported
      // as content changes, so parents that map contentChange to an
      // unsaved-changes flag are not marked dirty by their own data load.
      if (change && change.origin === 'setValue') { return; }

      this.contentChange.emit(this.content);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // If the 'content' value is changed by the parent component, update the
    // markdown editor.
    // We must be careful to only update the editor if the content was changed
    // externally, not due to edits made within the editor itself, because
    // calling .value() with an argument will move the cursor to the start of
    // the input, even if the value does not change.
    if (this.markdownEditor) {
      if (this.markdownEditor.value() !== this.content) {
        this.markdownEditor.value(changes.content.currentValue);
      }
    }
  }

}
